import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeIcon, ArrowRightIcon, XIcon, ClockIcon, ZapIcon, CheckCircleIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { useCurrencyByIP } from '../../hooks/useCurrencyByIP';
import { trackPixelEvent } from '../../hooks/useMetaPixel';
// --- Mini View Counter Component ---
function MiniViewCounter() {
  const [count, setCount] = useState(0);
  const [progress, setProgress] = useState(0);
  const [cycle, setCycle] = useState(0);
  const target = 1500000;
  const duration = 2500;
  const pauseBetween = 800;
  useEffect(() => {
    let startTime: number | null = null;
    let rafId: number;
    let pauseTimer: ReturnType<typeof setTimeout>;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const rawProgress = Math.min((timestamp - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - rawProgress, 4);
      setCount(Math.floor(ease * target));
      setProgress(rawProgress);
      if (rawProgress < 1) {
        rafId = requestAnimationFrame(animate);
      } else {
        pauseTimer = setTimeout(() => {
          setCount(0);
          setProgress(0);
          setCycle(c => c + 1);
        }, pauseBetween);
      }
    };
    rafId = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(pauseTimer);
    };
  }, [cycle]);
  const heat = Math.max(0, Math.min(1, (progress - 0.4) / 0.6));
  // Simplified color calc — fewer inline style recalculations
  const numberColor = heat > 0.7 ? '#FF4500' : heat > 0.3 ? '#FF8C00' : '#ffffff';
  const textShadow = heat > 0.3 ? `0 0 ${10 + heat * 20}px rgba(255, 107, 0, ${heat * 0.6})` : 'none';
  return <div className="flex items-center gap-3 md:gap-4" data-id="element-267">
      <div className="relative flex-shrink-0" data-id="element-268">
        {/* Simplified glow — no dynamic blur on mobile */}
        <div className="absolute inset-0 rounded-full bg-accent/30 blur-md md:blur-lg" style={{
        transform: `scale(${1 + heat})`,
        opacity: 0.4 + heat * 0.4
      }} data-id="element-269" />
        <EyeIcon className="relative w-6 h-6 md:w-8 md:h-8" style={{
        color: heat > 0.5 ? '#FF4500' : '#FF6B00'
      }} data-id="element-270" />
      </div>
      <div className="flex flex-col leading-none" data-id="element-271">
        <span className="text-xl md:text-3xl font-extrabold tabular-nums tracking-tight" style={{
        color: numberColor,
        textShadow
      }} data-id="element-272">
          {count.toLocaleString('en-US')}
        </span>
        <span className="text-[9px] md:text-xs font-bold uppercase tracking-widest text-accent" data-id="element-273">
          Visualizaciones
        </span>
      </div>
    </div>;
}
// --- Mini Countdown Display ---
function MiniCountdownDisplay({
  timeLeft
}: {
  timeLeft: number;
}) {
  const mins = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const secs = (timeLeft % 60).toString().padStart(2, '0');
  const isUrgent = timeLeft < 60;
  return <div className={`text-xs md:text-sm font-mono font-medium ${isUrgent ? 'text-red-400 animate-pulse' : 'text-neutral-400'}`} data-id="element-274">
      Expira en{' '}
      <span className="font-bold text-white" data-id="element-275">
        {mins}:{secs}
      </span>
    </div>;
}
// --- Urgency Popup ---
function UrgencyPopup({
  timeLeft,
  onClose,
  price,
  originalPrice
}: {
  timeLeft: number;
  onClose: () => void;
  price: string;
  originalPrice: string;
}) {
  const mins = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const secs = (timeLeft % 60).toString().padStart(2, '0');
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3
  }} className="fixed inset-0 z-[60] flex items-center justify-center px-5" onClick={() => {
    trackPixelEvent('AddToCart', {
      content_name: 'Hook Arsenal',
      content_type: 'product',
      value: 17.0,
      currency: 'USD'
    });
    window.open('https://pay.hotmart.com/P104435106G', '_blank');
    onClose();
  }} data-id="element-276">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-277" />

      {/* Modal */}
      <motion.div initial={{
      scale: 0.85,
      opacity: 0,
      y: 30
    }} animate={{
      scale: 1,
      opacity: 1,
      y: 0
    }} exit={{
      scale: 0.9,
      opacity: 0,
      y: 20
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 25
    }} onClick={e => e.stopPropagation()} className="relative w-full max-w-md bg-surface border border-accent/30 rounded-2xl overflow-hidden shadow-2xl" data-id="element-278">
        {/* Top glow */}
        <div className="absolute top-0 left-0 right-0 h-32 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at top center, rgba(255,107,0,0.12) 0%, transparent 70%)'
      }} data-id="element-279" />

        {/* Close button */}
        <a href="https://pay.hotmart.com/P104435106G" target="_blank" rel="noopener noreferrer" onClick={() => {
        trackPixelEvent('AddToCart', {
          content_name: 'Hook Arsenal',
          content_type: 'product',
          value: 17.0,
          currency: 'USD'
        });
      }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors cursor-pointer" data-id="element-280">
          <XIcon className="w-4 h-4 text-neutral-400" data-id="element-281" />
        </a>

        <div className="relative p-6 md:p-8" data-id="element-282">
          {/* Clock icon */}
          <div className="flex justify-center mb-4" data-id="element-283">
            <div className="relative" data-id="element-284">
              <div className="absolute inset-0 bg-accent/30 blur-xl rounded-full" data-id="element-285" />
              <div className="relative w-14 h-14 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center" data-id="element-286">
                <ClockIcon className="w-7 h-7 text-accent" data-id="element-287" />
              </div>
            </div>
          </div>

          {/* Headline */}
          <h3 className="text-2xl md:text-3xl font-extrabold text-white text-center mb-2 leading-tight" data-id="element-288">
            ¿Necesitas más{' '}
            <span className="font-serif-italic text-accent-light" data-id="element-289">tiempo</span>?
          </h3>

          <p className="text-neutral-400 text-center text-sm md:text-base mb-6" data-id="element-290">
            Ingresa aquí para no perder la oferta
          </p>

          {/* Countdown in popup */}
          <div className="flex items-center justify-center gap-2 mb-6" data-id="element-291">
            <div className="bg-bg border border-border rounded-lg px-3 py-2" data-id="element-292">
              <span className="text-2xl font-extrabold text-red-400 font-mono" data-id="element-293">
                {mins}
              </span>
            </div>
            <span className="text-xl font-bold text-accent animate-pulse" data-id="element-294">
              :
            </span>
            <div className="bg-bg border border-border rounded-lg px-3 py-2" data-id="element-295">
              <span className="text-2xl font-extrabold text-red-400 font-mono" data-id="element-296">
                {secs}
              </span>
            </div>
            <span className="text-sm text-neutral-500 ml-2" data-id="element-297">restantes</span>
          </div>

          {/* Offer details */}
          <div className="bg-bg/60 border border-border rounded-xl p-5 mb-6 space-y-3" data-id="element-298">
            <div className="flex items-center gap-2 mb-3" data-id="element-299">
              <ZapIcon className="w-4 h-4 text-accent" data-id="element-300" />
              <span className="text-sm font-bold text-accent uppercase tracking-wider" data-id="element-301">
                Hook Arsenal — Oferta Especial
              </span>
            </div>

            <div className="space-y-2" data-id="element-302">
              <div className="flex items-start gap-2" data-id="element-303">
                <CheckCircleIcon className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" data-id="element-304" />
                <span className="text-sm text-neutral-300" data-id="element-305">
                  200 ganchos en Español MX
                </span>
              </div>
              <div className="flex items-start gap-2" data-id="element-306">
                <CheckCircleIcon className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" data-id="element-307" />
                <span className="text-sm text-neutral-300" data-id="element-308">
                  Plantillas de apertura listas
                </span>
              </div>
              <div className="flex items-start gap-2" data-id="element-309">
                <CheckCircleIcon className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" data-id="element-310" />
                <span className="text-sm text-neutral-300" data-id="element-311">
                  Fórmula 1–3–1 + Selector rápido
                </span>
              </div>
              <div className="flex items-start gap-2" data-id="element-312">
                <CheckCircleIcon className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" data-id="element-313" />
                <span className="text-sm text-neutral-300" data-id="element-314">
                  Checklist + Antes/Después
                </span>
              </div>
            </div>

            {/* Price — localized */}
            <div className="pt-3 border-t border-border/60 flex items-baseline gap-3 flex-wrap" data-id="element-315">
              <span className="text-neutral-500 line-through text-sm" data-id="element-316">
                {originalPrice}
              </span>
              <span className="text-3xl font-extrabold text-white" data-id="element-317">
                {price}
              </span>
              <span className="text-xs font-bold text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full ml-auto" data-id="element-318">
                -82%
              </span>
            </div>
          </div>

          {/* CTA */}
          <CTAButton className="w-full py-4 text-lg" data-id="element-319">
            ASEGURAR MI OFERTA AHORA
          </CTAButton>

          <p className="text-xs text-neutral-500 text-center mt-4" data-id="element-320">
            Acceso inmediato · Descarga al instante
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
// --- Main Sticky Footer ---
export function StickyFooter() {
  const [isVisible, setIsVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(5 * 60);
  const [showPopup, setShowPopup] = useState(false);
  const popupShownRef = useRef(false);
  const {
    price,
    originalPrice
  } = useCurrencyByIP();
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    if (timeLeft <= 288 && !popupShownRef.current) {
      popupShownRef.current = true;
      setShowPopup(true);
    }
  }, [timeLeft]);
  return <>
      <AnimatePresence data-id="element-321">
        {showPopup && <UrgencyPopup timeLeft={timeLeft} onClose={() => setShowPopup(false)} price={price} originalPrice={originalPrice} data-id="element-322" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-323">
        {isVisible && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 260,
        damping: 20
      }} className="fixed bottom-0 left-0 right-0 z-50 px-3 pb-3 pt-1 md:px-4 md:pb-4 md:pt-2 md:p-0" data-id="element-324">
            <div className="max-w-4xl mx-auto" data-id="element-325">
              {/* Solid bg on mobile (no backdrop-blur), blur on desktop */}
              <div className="bg-surface/98 md:bg-surface/95 mobile-no-blur md:backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/50 rounded-2xl md:rounded-t-2xl md:rounded-b-none p-3 md:px-8 md:py-5 flex flex-col md:flex-row items-center justify-between gap-3 md:gap-8" data-id="element-326">
                {/* Left Side: Counter */}
                <div className="flex items-center justify-between w-full md:w-auto gap-4" data-id="element-327">
                  <MiniViewCounter data-id="element-328" />
                  <div className="md:hidden" data-id="element-329">
                    <MiniCountdownDisplay timeLeft={timeLeft} data-id="element-330" />
                  </div>
                </div>

                {/* Right Side: CTA + Desktop Countdown */}
                <div className="flex items-center gap-6 w-full md:w-auto justify-end" data-id="element-331">
                  <div className="hidden md:block text-right" data-id="element-332">
                    <div className="text-xs text-neutral-400 mb-1" data-id="element-333">
                      Oferta por tiempo limitado
                    </div>
                    <MiniCountdownDisplay timeLeft={timeLeft} data-id="element-334" />
                  </div>

                  <CTAButton className="w-full md:w-auto py-3 px-5 md:px-6 text-sm md:text-lg shadow-lg shadow-accent/20 !m-0" data-id="element-335">
                    LO QUIERO — {price}
                  </CTAButton>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}